import { Button, Modal } from 'antd';
import { memo, useCallback, useContext, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { UNSAFE_DataRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { usePreviewFileId } from '../../../hooks/usePreviewFileId';
import {
  isPreviewPushedState,
  parsePreviewFileIdFromPath,
  stripPreviewPath,
} from '../../../lib/previewRoutePath';
import { Icon } from '../../Core/Icon';
import { getFilegrantViewerBaseUrl } from '../../../lib/filegrantViewerChoice';
import FilegrantViewerHost from '../../../routes/Viewer/FilegrantViewerHost';
import Viewer from '../../../routes/Viewer/Viewer';
import { RIGHT_PANEL_WIDTH_PX, VIEWER_MODAL_Z_INDEX } from '../../../store/viewerModalSlice';
import { useAppStore } from '../../../store/store';

/** Centered preview modal — large for PDF/text reading; small margin vs viewport. */
const VIEWER_MODAL_WIDTH = 'min(98vw, 1680px)';
const VIEWER_MODAL_BODY_HEIGHT = 'min(92vh, 1080px)';

const noopSubscribe = () => () => {};

/**
 * Nested Details/AI panel. Separate component so FilegrantViewerModal body (Host/Viewer)
 * does not re-render on every `rightPanelContent` change (Monaco remount risk).
 */
function ViewerModalSidePanel() {
  const { t } = useTranslation();
  const isRightPanelOpen = useAppStore((s) => s.isRightPanelOpen);
  const rightPanelContent = useAppStore((s) => s.rightPanelContent);
  const hasRightPanelCloseButton = useAppStore((s) => s.hasRightPanelCloseButton);
  const closeRightPanel = useAppStore((s) => s.closeRightPanel);

  if (!isRightPanelOpen) return null;

  return (
    <aside
      data-testid="viewer-modal-side-panel"
      aria-label={t('a11y.detailsPanel')}
      className="border-gray-medium relative flex h-full shrink-0 flex-col overflow-hidden border-l bg-white"
      style={{ width: RIGHT_PANEL_WIDTH_PX, maxWidth: '100%' }}
    >
      {hasRightPanelCloseButton ? (
        <div className="absolute top-1 right-1 z-40 flex shrink-0 items-center">
          <Button
            type="text"
            aria-label={t('a11y.closePanel')}
            onClick={closeRightPanel}
            icon={<Icon name="XIcon" className="h-4 w-4" />}
          />
        </div>
      ) : null}
      <div
        className={`relative h-full min-h-0 overflow-auto px-2 ${hasRightPanelCloseButton ? 'pt-8' : ''}`}
      >
        {rightPanelContent}
      </div>
    </aside>
  );
}

/** Viewer host — memoized so panel open/close on parent does not re-render Monaco. */
const ViewerModalHost = memo(function ViewerModalHost({
  fileId,
  onRequestClose,
}: {
  fileId: string;
  onRequestClose: () => void;
}) {
  // Stashed by `useOpenFile` before navigating; ignored when it belongs to another file (deep link).
  const hasMeta = useAppStore((s) => s.viewerModalFileId === fileId);
  const version = useAppStore((s) => (hasMeta ? s.viewerModalVersion : null));
  const publicFileToken = useAppStore((s) => (hasMeta ? s.viewerModalPublicFileToken : null));
  const acToken = useAppStore((s) => (hasMeta ? s.viewerModalAcToken : null));
  const isReadOnly = useAppStore((s) => (hasMeta ? s.viewerModalIsReadOnly : false));

  const viewerBaseUrl = getFilegrantViewerBaseUrl();

  return (
    <div className="relative h-full min-h-0 min-w-0 flex-1 overflow-hidden">
      {viewerBaseUrl ? (
        <FilegrantViewerHost
          viewerBaseUrl={viewerBaseUrl}
          fileId={fileId}
          version={version}
          publicFileToken={publicFileToken}
          acToken={acToken}
          isReadOnly={isReadOnly}
          fillParent
          onRequestClose={onRequestClose}
        />
      ) : (
        <Viewer fileId={fileId} fillParent onRequestClose={onRequestClose} />
      )}
    </div>
  );
});

/**
 * Route-driven preview modal: `open` derives from `/files/.../preview/:fileId` only.
 * Open = push the preview URL (`useOpenFile`); close = leave it (back or strip).
 * Details / AI render as a nested right split inside the modal.
 */
export default function FilegrantViewerModal() {
  const navigate = useNavigate();
  const contextLocation = useLocation();
  const router = useContext(UNSAFE_DataRouterContext)?.router;
  const contextPreviewFileId = usePreviewFileId();
  // Read the data router's state directly: uSES commits the modal on a sync lane, ahead of
  // RouterProvider's `startTransition` re-render of the whole routed tree (~100ms+ in dev).
  const previewFileId = useSyncExternalStore(router?.subscribe ?? noopSubscribe, () =>
    router ? parsePreviewFileIdFromPath(router.state.location.pathname) : contextPreviewFileId,
  );
  const location = router?.state.location ?? contextLocation;
  const isRightPanelOpen = useAppStore((s) => s.isRightPanelOpen);
  const closeRightPanel = useAppStore((s) => s.closeRightPanel);

  // URL flips `open` false immediately; keep last id so the body renders through the exit animation.
  const [lastFileId, setLastFileId] = useState<string | null>(previewFileId ?? null);
  if (previewFileId && previewFileId !== lastFileId) {
    setLastFileId(previewFileId);
  }
  const fileId = previewFileId ?? lastFileId;

  const handleCloseModal = useCallback(() => {
    if (!previewFileId) return;
    closeRightPanel();
    if (isPreviewPushedState(location.state)) {
      navigate(-1);
      return;
    }
    navigate(stripPreviewPath(location.pathname), { replace: true });
  }, [closeRightPanel, location.pathname, location.state, navigate, previewFileId]);

  // `afterOpenChange(false)` fires after every exit animation; rc-dialog's `afterClose` is skipped
  // when its internal state is stale. Browser Back skips `handleCloseModal`, so close the panel here.
  const handleAfterOpenChange = useCallback(
    (open: boolean) => {
      if (open) return;
      closeRightPanel();
      setLastFileId(null);
    },
    [closeRightPanel],
  );

  return (
    <Modal
      open={previewFileId != null}
      onCancel={handleCloseModal}
      afterOpenChange={handleAfterOpenChange}
      centered
      width={VIEWER_MODAL_WIDTH}
      title={null}
      footer={null}
      closable={false}
      // Hidden content is memoized by rc-dialog (never re-renders), so unmount it after exit.
      destroyOnHidden
      // No zoom/fade: rc-motion's prepare→start→active frames delayed first paint by ~80ms.
      transitionName=""
      maskTransitionName=""
      zIndex={VIEWER_MODAL_Z_INDEX}
      rootClassName="filegrant-viewer-modal-root"
      classNames={{ container: 'filegrant-viewer-modal' }}
      styles={{
        container: {
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        },
        header: { display: 'none', padding: 0, margin: 0, border: 'none', minHeight: 0 },
        body: {
          display: 'flex',
          flexDirection: 'column',
          height: VIEWER_MODAL_BODY_HEIGHT,
          maxHeight: VIEWER_MODAL_BODY_HEIGHT,
          minHeight: 0,
          overflow: 'hidden',
          padding: 0,
          position: 'relative',
        },
      }}
    >
      <div className="flex h-full min-h-0 w-full flex-row" data-testid="filegrant-viewer-modal">
        {fileId ? <ViewerModalHost fileId={fileId} onRequestClose={handleCloseModal} /> : null}
        {isRightPanelOpen ? <ViewerModalSidePanel /> : null}
      </div>
    </Modal>
  );
}
